import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { forkJoin, Subject, skip, takeUntil } from 'rxjs';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { SelectOption } from 'src/app/components/form/select-field/select-field.config';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { RequestsService } from 'src/app/services/api/requests.service';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { AlertInfo } from 'src/app/models/alert-info';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';

type TrafficColor = 'green' | 'yellow' | 'red';
type TrafficStatus = 'GREEN' | 'YELLOW' | 'RED';

@Component({
  selector: 'app-waiting-time',
  imports: [CommonModule, ReactiveFormsModule, InputFieldComponent, SelectFieldComponent, NgbAlert],
  templateUrl: './waiting-time.component.html',
  styleUrl: './waiting-time.component.scss',
})
export class WaitingTimeComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  form!: FormGroup;
  readonly sections: TrafficColor[] = ['green', 'yellow', 'red'];
  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  trafficLightIds: Record<TrafficColor, number> = { green: 0, yellow: 0, red: 0 };
  userOptions: Record<TrafficColor, SelectOption[]> = { green: [], yellow: [], red: [] };

  private existingUserIds: Record<TrafficColor, Set<string>> = {
    green: new Set(), yellow: new Set(), red: new Set(),
  };
  private originalUserIds: Record<TrafficColor, string[]> = { green: [], yellow: [], red: [] };
  private userDataMap: Record<TrafficColor, Map<string, { name: string; role: string }>> = {
    green: new Map(), yellow: new Map(), red: new Map(),
  };

  private readonly statusColorMap: Record<TrafficStatus, TrafficColor> = {
    GREEN: 'green', YELLOW: 'yellow', RED: 'red',
  };

  constructor(
    private requestsService: RequestsService,
    private confirmationDialogService: ConfirmationDialogService,
    private alertNotificationService: AlertNotificationService,
    private mainLayoutService: MainLayoutService,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadSettings();

    this.mainLayoutService.activeProfile$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(() => this.loadSettings());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.form = new FormGroup({
      green: new FormGroup({ time: new FormControl('', Validators.required), notifications: new FormArray([]) }),
      yellow: new FormGroup({ time: new FormControl('', Validators.required), notifications: new FormArray([]) }),
      red: new FormGroup({ time: new FormControl('', Validators.required), notifications: new FormArray([]) }),
    });
  }

  private loadSettings(): void {
    this.requestsService.listWaitingTimeSettings().subscribe({
      next: (response: any) => {
        const trafficLights: any[] = response.data.requestTrafficLights;
        trafficLights.forEach(tl => {
          const color = this.statusColorMap[tl.status as TrafficStatus];
          if (color) {
            this.trafficLightIds[color] = tl.requestTrafficLightId;
            this.getTimeControl(color).setValue(this.minutesToTime(tl.duration));
            this.loadColorData(color, tl.requestTrafficLightId);
          }
        });
      },
      error: () => {
        this.showAlertInfo('danger', 'An error occurred while loading settings.');
      }
    });
  }

  private loadColorData(color: TrafficColor, trafficLightId: number): void {
    forkJoin({
      users: this.requestsService.listWaitingTimeUsers(trafficLightId),
      available: this.requestsService.listWaitingTimeAvailableUsers(trafficLightId),
    }).subscribe({
      next: ({ users, available }) => {
        const existingUsers: any[] = users.data.users;
        const availableUsers: any[] = available.data.users;

        this.userDataMap[color].clear();
        [...existingUsers, ...availableUsers].forEach(u => {
          this.userDataMap[color].set(String(u.userId), {
            name: `${u.firstName} ${u.lastName}`,
            role: u.userCode ?? '',
          });
        });

        this.userOptions[color] = [...existingUsers, ...availableUsers].map(u => ({
          key: String(u.userId),
          value: `${u.firstName} ${u.lastName}`,
        }));

        this.existingUserIds[color] = new Set(existingUsers.map(u => String(u.userId)));
        this.originalUserIds[color] = existingUsers.map(u => String(u.userId));

        const notifications = this.getNotificationsArray(color);
        notifications.clear();
        existingUsers.forEach(u => notifications.push(this.buildNotificationGroup({
          userId: String(u.userId),
          role: u.userCode ?? '',
        })));
      },
      error: () => {
        this.showAlertInfo('danger', `An error occurred while loading users for ${color}.`);
      }
    });
  }

  buildForm(settings: any): void {
    this.form = new FormGroup({
      green: this.buildSectionGroup(settings.green),
      yellow: this.buildSectionGroup(settings.yellow),
      red: this.buildSectionGroup(settings.red),
    });
  }

  private buildSectionGroup(section: any): FormGroup {
    return new FormGroup({
      time: new FormControl(section.time, Validators.required),
      notifications: new FormArray(
        (section.notifications as any[]).map(n => this.buildNotificationGroup(n))
      )
    });
  }

  private buildNotificationGroup(n: any = { userId: '', role: '' }): FormGroup {
    return new FormGroup({
      userId: new FormControl(String(n.userId || ''), Validators.required),
      role: new FormControl(n.role || ''),
    });
  }

  getSectionGroup(color: TrafficColor): FormGroup {
    return this.form.get(color) as FormGroup;
  }

  getTimeControl(color: TrafficColor): FormControl {
    return this.getSectionGroup(color).get('time') as FormControl;
  }

  getNotificationsArray(color: TrafficColor): FormArray {
    return this.getSectionGroup(color).get('notifications') as FormArray;
  }

  getRowIndexes(color: TrafficColor): number[] {
    const count = this.getNotificationsArray(color).length;
    return Array.from({ length: Math.max(count, 1) }, (_, i) => i);
  }

  getNotificationGroup(color: TrafficColor, index: number): FormGroup {
    return this.getNotificationsArray(color).at(index) as FormGroup;
  }

  getUserIdControl(color: TrafficColor, index: number): FormControl {
    return this.getNotificationGroup(color, index).get('userId') as FormControl;
  }

  getRoleControl(color: TrafficColor, index: number): FormControl {
    return this.getNotificationGroup(color, index).get('role') as FormControl;
  }

  onUserSelect(color: TrafficColor, index: number, selectedUserId: any): void {
    const userData = this.userDataMap[color].get(String(selectedUserId));
    if (userData) {
      this.getRoleControl(color, index).setValue(userData.role);
    }
  }

  addNotification(color: TrafficColor): void {
    this.getNotificationsArray(color).push(this.buildNotificationGroup());
  }

  confirmRemoveNotification(color: TrafficColor, index: number): void {
    const userId = this.getUserIdControl(color, index).value;
    const isExisting = !!userId && this.existingUserIds[color].has(String(userId));

    this.confirmationDialogService.open(
      'Are you sure you want to remove this notification recipient?',
      () => {
        if (isExisting) {
          const trafficLightId = this.trafficLightIds[color];
          this.requestsService.deleteWaitingTimeUser(trafficLightId, Number(userId)).subscribe({
            next: () => {
              this.getNotificationsArray(color).removeAt(index);
              this.existingUserIds[color].delete(String(userId));
              this.alertNotificationService.success('Recipient removed successfully.');
              this.requestsService.listWaitingTimeAvailableUsers(trafficLightId).subscribe({
                next: (res: any) => {
                  const availableUsers: any[] = res.data.users;
                  availableUsers.forEach(u => {
                    const key = String(u.userId);
                    if (!this.userDataMap[color].has(key)) {
                      this.userDataMap[color].set(key, {
                        name: `${u.firstName} ${u.lastName}`,
                        role: u.userCode ?? '',
                      });
                    }
                  });
                  const existingOptions = this.userOptions[color].filter(o =>
                    this.existingUserIds[color].has(o.key)
                  );
                  this.userOptions[color] = [
                    ...existingOptions,
                    ...availableUsers.map(u => ({ key: String(u.userId), value: `${u.firstName} ${u.lastName}` })),
                  ];
                }
              });
            },
            error: () => {
              this.showAlertInfo('danger', 'An error occurred while removing the user.');
            }
          });
        } else {
          this.getNotificationsArray(color).removeAt(index);
          this.alertNotificationService.success('Recipient removed successfully.');
        }
      }
    );
  }

  getSectionLabel(color: TrafficColor): string {
    return `${color.toUpperCase()} TRAFFICLIGHT`;
  }

  saveSettings(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const postCalls: any[] = [];
    const putCalls: any[] = [];
    const durationCalls: { trafficLightId: number; duration: number }[] = [];

    this.sections.forEach(color => {
      const trafficLightId = this.trafficLightIds[color];
      const timeControl = this.getTimeControl(color);
      if (timeControl.dirty) {
        durationCalls.push({ trafficLightId, duration: this.timeToMinutes(timeControl.value) });
      }

      const notifications = this.getNotificationsArray(color).getRawValue();
      notifications.forEach((n: any, index: number) => {
        if (!n.userId) return;
        const currentUserId = String(n.userId);
        const originalUserId = this.originalUserIds[color][index];

        if (originalUserId) {
          if (currentUserId !== originalUserId) {
            putCalls.push({ trafficLightId, originalUserId: Number(originalUserId), userId: Number(currentUserId) });
          }
        } else {
          if (!this.existingUserIds[color].has(currentUserId)) {
            postCalls.push({ trafficLightId, userId: Number(currentUserId) });
          }
        }
      });
    });

    if (postCalls.length === 0 && putCalls.length === 0 && durationCalls.length === 0) {
      this.showAlertInfo('success', 'Settings saved successfully.');
      return;
    }

    forkJoin([
      ...durationCalls.map(c => this.requestsService.updateWaitingTimeSettings(c.trafficLightId, { duration: c.duration })),
      ...postCalls.map(c => this.requestsService.createWaitingTimeUser(c.trafficLightId, { userId: c.userId })),
      ...putCalls.map(c => this.requestsService.updateWaitingTimeUser(c.trafficLightId, c.originalUserId, { userId: c.userId })),
    ]).subscribe({
      next: () => {
        this.showAlertInfo('success', 'Settings saved successfully.');
        this.sections.forEach(color => this.getTimeControl(color).markAsPristine());
        this.sections.forEach(color => this.loadColorData(color, this.trafficLightIds[color]));
      },
      error: () => {
        this.showAlertInfo('danger', 'An error occurred while saving the settings.');
      }
    });
  }

  private minutesToTime(minutes: number): string {
    const h = Math.floor(minutes / 60).toString().padStart(2, '0');
    const m = (minutes % 60).toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  private timeToMinutes(time: string): number {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  }

  private showAlertInfo(type: string, message: string): void {
    this.alertInfo = { show: true, type, message };
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => { this.alertInfo.show = false; }, 3000);
  }
}

